import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Clock, DollarSign, Activity, Users as UsersIcon, ChevronRight } from 'lucide-react';
import { useRTL } from '@/hooks/useRTL';
import { ADMIN_COLORS } from '@/features/admin/utils/adminAnimations';
import { useTilt } from '@/hooks/useTilt';
import type { WidgetProps } from '../widgetRegistry';

function ActionCard({
  to,
  icon,
  title,
  subtitle,
  isRTL,
  compact,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  isRTL: boolean;
  compact?: boolean;
}) {
  const tiltRef = useRef<HTMLDivElement>(null);
  const { onMouseMove, onMouseLeave } = useTilt(tiltRef, { maxTilt: 3, scale: 1.015 });

  return (
    <Link to={to}>
      <div
        ref={tiltRef}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        className={`rounded-xl transition-all duration-200 hover:translate-y-[-2px] flex items-center gap-3 border border-white/30 glass-surface ${compact ? 'p-2.5' : 'p-4'}`}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = ADMIN_COLORS.shadowMd)}
        onMouseDown={(e) => (e.currentTarget.style.boxShadow = ADMIN_COLORS.shadow)}
      >
        <div
          className={`rounded-lg flex items-center justify-center flex-shrink-0 ${compact ? 'w-7 h-7' : 'w-9 h-9'}`}
          style={{ backgroundColor: ADMIN_COLORS.primarySoft }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-medium ${compact ? 'text-xs' : 'text-sm'}`} style={{ color: ADMIN_COLORS.navy }}>
            {title}
          </h3>
          {subtitle && !compact && (
            <p className="text-xs" style={{ color: ADMIN_COLORS.textSecondary }}>
              {subtitle}
            </p>
          )}
        </div>
        <ChevronRight
          className="w-4 h-4 flex-shrink-0"
          style={{
            color: ADMIN_COLORS.textTertiary,
            transform: isRTL ? 'scaleX(-1)' : undefined,
          }}
        />
      </div>
    </Link>
  );
}

export function AdminQuickActionsWidget({ height }: WidgetProps) {
  const { t } = useTranslation();
  const isRTL = useRTL();
  const compact = height < 200;

  return (
    <div className={`grid gap-3 ${compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
      <ActionCard
        to="/admin/approvals"
        icon={<Clock className={`${compact ? 'w-4 h-4' : 'w-5 h-5'}`} style={{ color: ADMIN_COLORS.primary }} />}
        title={t('admin.pendingApprovals')}
        isRTL={isRTL}
        compact={compact}
      />
      <ActionCard
        to="/admin/payroll"
        icon={<DollarSign className={`${compact ? 'w-4 h-4' : 'w-5 h-5'}`} style={{ color: ADMIN_COLORS.primary }} />}
        title={t('admin.payrollManagement')}
        isRTL={isRTL}
        compact={compact}
      />
      <ActionCard
        to="/admin/activity"
        icon={<Activity className={`${compact ? 'w-4 h-4' : 'w-5 h-5'}`} style={{ color: ADMIN_COLORS.primary }} />}
        title={t('audit.title')}
        isRTL={isRTL}
        compact={compact}
      />
      <ActionCard
        to="/admin/users"
        icon={<UsersIcon className={`${compact ? 'w-4 h-4' : 'w-5 h-5'}`} style={{ color: ADMIN_COLORS.primary }} />}
        title={t('users.title')}
        isRTL={isRTL}
        compact={compact}
      />
    </div>
  );
}
